import * as React from 'react';
import { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FaLock } from 'react-icons/fa';

import { cn } from '@/lib/utils';
import type * as Select from '@radix-ui/react-select';
import { useDebounce, useIntersectionObserver } from '@uidotdev/usehooks';
import { Check, ChevronDown, ChevronsUpDown, ChevronUp, LoaderCircle, Plus, X } from 'lucide-react';

import { buttonVariants } from './button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './collapsible';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './command';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

export type SelectChangeNativeEvent = {
  target: {
    value: string | string[];
    name: string;
  };
};

export type OptionData<T = any> = (
  | {
    value: string;
    label: React.ReactNode;
    disabled?: boolean;
    readable?: string;
    key?: string;
    [other: string]: unknown;
  }
  | {
    value: string;
    label: string | React.ReactNode;
    disabled?: boolean;
    readable?: string;
    key?: string;
    [other: string]: unknown;
  }
) & { data?: T };

export type ComboBoxItem = {
  label: string;
  value: string;
};

export type ComboBoxProps<T> = Omit<Select.SelectProps, 'onValueChange'> & {
  placeholder?: string;
  selectAll?: boolean;
  searchPlaceholder?: string;
  options: T[];
  initialOptions?: T[];
  onValueChange?: (value: string, option: ComboBoxProps<T>['options'][number]) => void;
  onChange?: (value: SelectChangeNativeEvent) => void;
  emptyMessage?: string;
  variant?: 'normal' | 'outlined';
  isLoading?: boolean;
  control?: Control<any, any>;
  disabledOptions?: string[];
  className?: string;
  showClearButton?: boolean;
  onSearch?: (value: string) => void;
  onLoadMore?: () => void;
  showLoadMore?: boolean;
  mode?: 'single' | 'multi';
  value?: string | string[];
  categoryKey?: string;
  categoryRender?: (key: string) => React.ReactNode;
  triggerVariant?: 'outline' | 'underline';
  showAddRegister?: boolean;
  textAddRegister?: string;
  actionButtonAddRegister?: () => void;
  hasPermission?: boolean;
};

// Helper function to safely get nested object properties
const getNestedProperty = (obj: any, path: string) => {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

const Combo = <T extends OptionData>(
  {
    hasPermission = true,
    options = [] as T[],
    placeholder,
    onValueChange,
    onChange,
    disabledOptions,
    value,
    searchPlaceholder,
    emptyMessage,
    name,
    className,
    disabled,
    isLoading,
    showClearButton = true,
    onSearch,
    onLoadMore,
    showLoadMore = false,
    initialOptions = [],
    mode = 'single',
    selectAll,
    categoryKey,
    categoryRender,
    triggerVariant = 'outline',
    showAddRegister = false,
    textAddRegister = '',
    actionButtonAddRegister,
    ..._
  }: ComboBoxProps<T>,
  ref: React.Ref<HTMLInputElement> | null,
) => {
  const { t } = useTranslation();
  const [loadMoreRef, entry] = useIntersectionObserver({
    threshold: 0,
    root: null,
    rootMargin: '0px',
  });

  const isDisabled = disabled || !hasPermission;

  const [open, setOpen] = React.useState(false);
  const [internalValue, setInternalValue] = React.useState<string | string[] | undefined>(undefined);
  const [filteredOptions, setFilteredOptions] = React.useState<T[]>([]);
  const [searchText, setSearchText] = React.useState<string>('');
  const [isTyping, setIsTyping] = React.useState(false);
  const [selectedOptions, setSelectedOptions] = React.useState<T[]>([]);
  const [openCategories, setOpenCategories] = React.useState<Record<string, boolean>>({});

  const search = useDebounce(searchText, 300);

  const isMulti = Array.isArray(value) || mode === 'multi';

  const selectedOption = React.useMemo(() => {
    const valToFind = internalValue ?? value;
    if (valToFind === undefined || valToFind === null || valToFind === '') return null;
    const allOpts = [...selectedOptions, ...initialOptions, ...options];

    if (isMulti) {
      const valArray = Array.isArray(valToFind) ? valToFind : [valToFind];
      return valArray
        .map(val =>
          allOpts.find(
            opt =>
              String(opt.value) === String(val) ||
              String(opt.value).toLowerCase() === String(val).toLowerCase(),
          ),
        )
        .filter(Boolean) as T[];
    }

    return (
      allOpts.find(
        opt =>
          String(opt.value) === String(valToFind) ||
          String(opt.value).toLowerCase() === String(valToFind).toLowerCase(),
      ) ?? null
    );
  }, [internalValue, isMulti, value, selectedOptions, initialOptions, options]);

  const selectedOptionData = React.useMemo(() => {
    const getParsedValues = (selectedOption: T) => {
      return {
        label: selectedOption.label,
        readable: selectedOption.readable,
      };
    };
    if (!selectedOption) return null;
    if (Array.isArray(selectedOption)) {
      if (selectedOption.length === 0) return null;
      const text = selectedOption
        .filter(option => !!option)
        .map(option => option.label)
        .join(', ');
      if (!text) return null;
      return getParsedValues({
        value: '',
        label: text,
        readable: text,
      } as T);
    }
    return getParsedValues(selectedOption);
  }, [selectedOption]);

  const filterOptions = React.useCallback(
    (opts: T[], searchVal: string) => {
      if (!searchVal.trim()) return opts;
      const normalizedSearch = searchVal
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

      return opts.filter(option => {
        if (initialOptions.some(initialOption => String(initialOption.value) === String(option.value))) {
          return false;
        }
        const labelText =
          typeof option.label === 'string'
            ? option.label
            : (option.readable ?? (typeof option.label === 'number' ? String(option.label) : ''));
        const valueText = typeof option.value === 'string' ? option.value : String(option.value ?? '');
        const combinedText = `${valueText} ${labelText}`;

        const normalizedText = combinedText
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');

        return normalizedText.includes(normalizedSearch);
      });
    },
    [initialOptions],
  );

  const handleFilter = (val: string) => {
    setIsTyping(true);
    setSearchText(val);
  };

  const resetValue = (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.KeyboardEvent<HTMLButtonElement>) => {
    const newValue: string | string[] = isMulti ? [] : '';
    e.stopPropagation();
    setInternalValue(newValue);
    onValueChange?.('', null as any);
    onChange?.({
      target: {
        value: newValue,
        name: name ?? '',
      },
    });
  };

  const handleSelectAll = () => {
    setInternalValue(options.map(option => option.value));
    onChange?.({
      target: {
        value: options.map(option => option.value),
        name: name ?? '',
      },
    });
    setSelectedOptions(options);
  };

  const handleSelect = (currentValue: string, option: T) => {
    const targetValue = option?.value ?? currentValue;
    const isCurrentlySelected = isMulti
      ? (Array.isArray(value) ? value : []).some(
          v => String(v).toLowerCase() === String(targetValue).toLowerCase(),
        )
      : String(value) === String(targetValue) || String(internalValue) === String(targetValue);

    const newValue = isCurrentlySelected ? (isMulti ? [] : '') : targetValue;
    const treatedValue = isMulti
      ? (Array.isArray(value) ? value : []).some(
          v => String(v).toLowerCase() === String(targetValue).toLowerCase(),
        )
        ? (value as string[]).filter(v => String(v).toLowerCase() !== String(targetValue).toLowerCase())
        : [...((value as string[]) || []), targetValue]
      : newValue;

    setInternalValue(treatedValue);
    onChange?.({
      target: {
        value: treatedValue,
        name: name ?? '',
      },
    });
    setSelectedOptions(old =>
      old.some(opt => String(opt.value) === String(targetValue))
        ? [...old.filter(opt => String(opt.value) !== String(targetValue))]
        : [...old, option],
    );
    onValueChange?.(treatedValue as string, option);

    if (mode !== 'multi') setOpen(false);
  };

  const toggleCategory = (category: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  const groupedOptions = React.useMemo(() => {
    if (!categoryKey) return { undefined: filteredOptions };

    const grouped = filteredOptions.reduce(
      (acc, option) => {
        const category = getNestedProperty(option, categoryKey) ?? 'other';
        if (!acc[category]) acc[category] = [];
        acc[category].push(option);
        return acc;
      },
      {} as Record<string, T[]>,
    );

    // Move 'other' category to the end if it exists
    if (grouped.other) {
      const { other } = grouped;
      delete grouped.other;
      grouped.other = other;
    }

    return grouped;
  }, [filteredOptions, categoryKey]);

  React.useEffect(() => {
    if (value?.toString() !== internalValue?.toString()) {
      setInternalValue(value);
      onChange?.({
        target: {
          value: value ?? (isMulti ? [] : ''),
          name: name ?? '',
        },
      });
    }
  }, [value]);

  React.useEffect(() => {
    onSearch?.(search);
    setIsTyping(false);
  }, [search]);

  React.useEffect(() => {
    if (onSearch) {
      setFilteredOptions(options);
    } else {
      setFilteredOptions(filterOptions(options, searchText));
    }
  }, [options, searchText, onSearch, filterOptions]);

  React.useEffect(() => {
    if (!open) {
      setSearchText('');
    }
  }, [open]);

  React.useEffect(() => {
    if (entry?.isIntersecting) {
      onLoadMore?.();
    }
  }, [entry?.isIntersecting]);

  React.useEffect(() => {
    if (categoryKey) {
      const initialOpenState = Object.keys(groupedOptions).reduce(
        (acc, category) => {
          acc[category] = true;
          return acc;
        },
        {} as Record<string, boolean>,
      );
      setOpenCategories(initialOpenState);
    }
  }, [categoryKey, groupedOptions]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <input
        name={name}
        value={value}
        onChange={() => null}
        className="absolute h-0 w-0"
        tabIndex={-1}
        ref={ref as any}
      />
      <PopoverTrigger asChild type="button" disabled={isDisabled} className="group/combo relative w-full">
        <button
          className={cn(
            placeholder && !selectedOptionData ? 'text-gray-400' : '',
            'text-gray-500',
            buttonVariants({ variant: triggerVariant === 'underline' ? 'underline' : 'outline' }),
            'h-10 w-[200px] justify-between overflow-hidden overflow-ellipsis whitespace-nowrap',
            (!placeholder && !selectedOption) ?? 'justify-end',
            className,
            isDisabled ? 'pointer-events-none cursor-not-allowed opacity-50 !text-gray-500' : '',
            triggerVariant === 'underline' ? '!py-1 !shadow-transparent' : 'py-2',
          )}
          type="button"
          aria-expanded={open}
          disabled={false}
          title={
            !hasPermission
              ? t('noPermission')
              : (selectedOptionData &&
                (typeof selectedOptionData.label === 'string'
                  ? selectedOptionData.label
                  : selectedOptionData.readable)) ||
              ''
          }
        >
          <span className={cn('overflow-hidden overflow-ellipsis whitespace-nowrap text-sm')}>
            {selectedOptionData ? selectedOptionData.label : placeholder}
          </span>
          {isLoading ? (
            <LoaderCircle size={18} className="animate-spin" />
          ) : value && showClearButton ? (
            <button
              onClick={resetValue}
              type="button"
              aria-label="clear"
              className="text-gray-500 opacity-50 focus:text-red-600 focus:opacity-100"
              title={t('clear')}
            >
              <X className="ml-2 h-4 w-4 shrink-0" />
            </button>
          ) : (
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-gray-500 opacity-50" />
          )}
          {!hasPermission && (
            <div
              className="absolute right-0 top-0 flex h-full w-full flex-col items-center justify-center text-xs"
              title={t('noPermission')}
            >
              <FaLock />
              {t('noPermission')}
            </div>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="relative z-[10000] max-h-[--radix-popover-content-available-height] w-[--radix-popover-trigger-width] min-w-[200px] p-0 font-normal">
        <Command shouldFilter={false}>
          <CommandInput placeholder={searchPlaceholder} onValueChange={handleFilter} value={searchText} />
          <CommandEmpty>
            {emptyMessage || (isLoading || isTyping ? `${t('loading')}...` : t('nothingToShow'))}
          </CommandEmpty>
          <CommandGroup className="flex max-h-[200px] w-full flex-col overflow-y-auto">
            <CommandList className="h-full max-h-full w-full">
              {selectAll && isMulti ? (
                <CommandItem
                  key="all"
                  value="all"
                  onSelect={handleSelectAll}
                  className={cn(
                    'dark:hover:bg-frx-blue-800 dark:aria-selected:bg-frx-blue-800',
                    value?.length === options?.length ? 'dark:aria-selected:bg-frx-blue-800' : '',
                  )}
                  disabled={value?.length === options?.length}
                >
                  <Check
                    className={cn('mr-2 h-4 w-4', value?.length === options?.length ? 'opacity-100' : 'opacity-0')}
                  />
                  {t('all')}
                </CommandItem>
              ) : null}
              {Object.entries(groupedOptions).map(([category, categoryOptions]) => (
                <React.Fragment key={category}>
                  {categoryKey && category !== 'undefined' ? (
                    <Collapsible open={openCategories[category]} onOpenChange={undefined}>
                      <CollapsibleTrigger asChild>
                        <CommandItem
                          value={`category-${category}`}
                          className="cursor-pointer font-semibold text-muted-foreground"
                          onSelect={undefined}
                        >
                          <div
                            className="flex w-full items-center justify-between"
                            onClick={e => {
                              e.stopPropagation();
                              toggleCategory(category, e);
                            }}
                          >
                            {categoryRender ? categoryRender(category) : category}
                            {openCategories[category] ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </div>
                        </CommandItem>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        {categoryOptions.map(option => {
                          const isSelected = isMulti
                            ? (Array.isArray(value) ? value : []).some(v => String(v) === String(option.value))
                            : String(value) === String(option.value) || String(internalValue) === String(option.value);
                          const itemSearchValue = `${option.value} ${typeof option.label === 'string' ? option.label : (option.readable ?? '')}`;
                          return (
                            <CommandItem
                              key={option.value}
                              value={itemSearchValue}
                              onSelect={() => handleSelect(option.value, option)}
                              className={cn(
                                'dark:hover:bg-frx-blue-800 dark:aria-selected:bg-frx-blue-800 pl-6',
                                isSelected ? 'dark:aria-selected:bg-frx-blue-800' : '',
                              )}
                              disabled={disabledOptions?.includes(option.value)}
                            >
                              <Check className={cn('mr-2 h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')} />
                              {option.label}
                            </CommandItem>
                          );
                        })}
                      </CollapsibleContent>
                    </Collapsible>
                  ) : (
                    categoryOptions.map(option => {
                      const isSelected = isMulti
                        ? (Array.isArray(value) ? value : []).some(v => String(v) === String(option.value))
                        : String(value) === String(option.value) || String(internalValue) === String(option.value);
                      const itemSearchValue = `${option.value} ${typeof option.label === 'string' ? option.label : (option.readable ?? '')}`;
                      return (
                        <CommandItem
                          key={option.value}
                          value={itemSearchValue}
                          onSelect={() => handleSelect(option.value, option)}
                          className={cn(
                            'dark:hover:bg-frx-blue-800 dark:aria-selected:bg-frx-blue-800',
                            isSelected ? 'dark:aria-selected:bg-frx-blue-800' : '',
                          )}
                          disabled={disabledOptions?.includes(option.value)}
                        >
                          <Check className={cn('mr-2 h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')} />
                          {option.label}
                        </CommandItem>
                      );
                    })
                  )}
                </React.Fragment>
              ))}
            </CommandList>
            {showLoadMore && !isTyping ? <div ref={loadMoreRef} /> : null}
          </CommandGroup>
          {showAddRegister && (
            <div className="flex justify-center p-2">
              <button
                type="button"
                className="w-full rounded border border-primary font-semibold text-primary hover:bg-primary hover:text-white"
                onClick={actionButtonAddRegister}
              >
                <div className="flex w-full items-center justify-center">
                  <Plus className="h-4 w-4" /> {textAddRegister}
                </div>
              </button>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const ComboBox = React.forwardRef(Combo);
